import { motion } from "framer-motion";
import { Music4, ShieldCheck, Sun, Users } from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    title: "Faith in every note",
    icon: Music4,
    color: "#FFC107",
    bg: "#FEF3C7",
    body: "Every song is written to plant scripture deep in little hearts — melodies they hum at breakfast become truths they carry for life.",
  },
  {
    n: "02",
    title: "Parent-first, always",
    icon: ShieldCheck,
    color: "#0284C7",
    bg: "#E0F2FE",
    body: "Grown-up accounts, moderated community, no direct messages, no ads aimed at kids. Safety architecture comes before growth.",
  },
  {
    n: "03",
    title: "Joyful daily rhythms",
    icon: Sun,
    color: "#16A34A",
    bg: "#DCFCE7",
    body: "Good-morning praise, afternoon activities and goodnight lullabies — content designed around the real rhythm of family life.",
  },
  {
    n: "04",
    title: "Growing faith together",
    icon: Users,
    color: "#9333EA",
    bg: "#F3E8FF",
    body: "A community of parents sharing crafts, prayers and encouragement — wrapped around the songs and characters your kids love.",
  },
];

const Manifesto = () => (
  <section className="relative mx-auto flex min-h-[100svh] w-full max-w-7xl flex-col justify-center px-4 py-28 sm:px-6 sm:py-36" data-testid="manifesto-section">
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className="text-xs font-bold uppercase tracking-[0.3em] text-slate-400"
    >
      Our promise
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: 0.1 }}
      className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
    >
      Helping little hearts sing, learn and grow in faith.
    </motion.h2>

    <div className="mt-14 grid gap-5 md:grid-cols-2">
      {CHAPTERS.map((c, i) => (
        <motion.article
          key={c.n}
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: i * 0.08 }}
          whileHover={{ y: -6, rotate: i % 2 ? 0.5 : -0.5 }}
          className={`group relative overflow-hidden rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.12)] transition-shadow hover:shadow-[0_20px_40px_-10px_rgba(14,165,233,0.2)] sm:p-10 ${
            i % 3 === 1 ? "md:translate-y-8" : ""
          }`}
          data-testid={`manifesto-chapter-${c.n}`}
        >
          <span
            className="pointer-events-none absolute -right-4 -top-8 font-display text-[9rem] font-bold leading-none opacity-[0.07] transition-opacity group-hover:opacity-[0.14]"
            aria-hidden="true"
          >
            {c.n}
          </span>
          <div
            className="mb-6 grid h-14 w-14 place-items-center rounded-2xl transition-transform group-hover:scale-110 group-hover:-rotate-6"
            style={{ backgroundColor: c.bg }}
          >
            <c.icon className="h-7 w-7" style={{ color: c.color }} />
          </div>
          <p className="font-display text-sm font-semibold tracking-widest" style={{ color: c.color }}>
            CHAPTER {c.n}
          </p>
          <h3 className="mt-2 font-display text-xl font-bold text-slate-900 sm:text-2xl">{c.title}</h3>
          <p className="mt-3 text-base text-slate-600 leading-relaxed">{c.body}</p>
        </motion.article>
      ))}
    </div>
  </section>
);

export default Manifesto;
